import Alpine from 'alpinejs'
import { Datepicker } from 'vanillajs-datepicker'
import nl from 'vanillajs-datepicker/locales/nl'

Object.assign(Datepicker.locales, nl)

type PriceRow = {
    rank: string
    priceCode: string
    priceLabel: string
    price: string
}

type ArrangementItem = {
    id: string
    title: string
    time: string
    date: string
    priceDisplay: string
    prices: PriceRow[]
}

const formatDateKey = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    return `${year}-${month}-${day}`
}

export const initPackageDatePicker = () => {
    Alpine.data(
        'packageDatePicker',
        ({ arrangementDates }: { arrangementDates: Record<string, ArrangementItem[]> }) => ({
            selectedArrangements: [] as ArrangementItem[],
            datePicker: null as Datepicker | null,

            getPricesByRank(prices: PriceRow[]): { rank: string; prices: PriceRow[] }[] {
                const grouped: Record<string, PriceRow[]> = {}
                for (const p of prices) {
                    if (!grouped[p.rank]) grouped[p.rank] = []
                    grouped[p.rank].push(p)
                }
                return Object.entries(grouped).map(([rank, prices]) => ({ rank, prices }))
            },

            init() {
                const dateInput = (this.$refs as Record<string, HTMLElement>).filterDatesInput as HTMLInputElement
                if (!dateInput) return

                const dateKeys = Object.keys(arrangementDates).sort()
                const maxDate = dateKeys.length ? new Date(dateKeys[dateKeys.length - 1]) : undefined

                const firstDate = dateKeys.length > 0 ? new Date(dateKeys[0]) : new Date()

                this.datePicker = new Datepicker(dateInput, {
                    format: 'dd-mm-yyyy',
                    language: 'nl',
                    minDate: new Date(),
                    maxDate,
                    defaultViewDate: firstDate,
                    datesDisabled: (date: Date, viewId: number) => {
                        if (viewId === 0) {
                            return !arrangementDates[formatDateKey(date)]?.length
                        }
                        if (viewId === 1) {
                            const year = date.getFullYear()
                            const month = (date.getMonth() + 1).toString().padStart(2, '0')
                            const prefix = `${year}-${month}-`
                            return !Object.keys(arrangementDates).some((key) => key.startsWith(prefix))
                        }
                        if (viewId === 2) {
                            const prefix = `${date.getFullYear()}-`
                            return !Object.keys(arrangementDates).some((key) => key.startsWith(prefix))
                        }
                        return false
                    },
                })

                dateInput.addEventListener('changeDate', (e: Event) => {
                    const customEvent = e as CustomEvent
                    if (!customEvent.detail?.date) {
                        this.selectedArrangements = []
                        return
                    }
                    const dateStr = formatDateKey(new Date(customEvent.detail.date))
                    this.selectedArrangements = arrangementDates[dateStr] ?? []
                    this.datePicker?.hide()
                })
            },
        })
    )
}
